import { trackEvent } from '@patrianna/analytics'
import { getDialogStack, openDialog, removeShownSessionDialog } from '@patrianna/dialog-manager'
import { getTranslationFromJson } from '@patrianna/shared-utils'
import { convertOpenApiErrorResponseToGateway } from '@patrianna-payments/shared-store/payments/utils/convertOpenApiErrorResponseToGateway'

import content from 'prismic-translates/common.json'
import getAccountBonusesRequest from 'services/gateway/requests/getAccountBonuses'
import { getSeonSessionId } from 'services/seon'
import { IS_BRAND_PULSZ } from 'src/config/brands'
import { ERROR_REASON_CODE_MAP } from 'src/containers/GlobalSnackbar/config'
import { IS_SUCCESS_PASSWORD_CHANGE, setDataToLocalStorage } from 'src/utils/localStorage'
import { isPlayPage } from 'src/utils/route'
import { enabledSweepstake, saveLocationData } from 'store/modules/appConfig/actions'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { initCurrencies, setActiveCurrency } from 'store/modules/currencies/actions'
import { addUnlockedGames, levelUpHandler } from 'store/modules/games/actions'
import { getConfirmedKycInfo } from 'store/modules/kyc/actions'
import { setOpenPrerequisites } from 'store/modules/prerequisites/actions'
import { getOffers } from 'store/modules/shop/actions'
import { getActiveGameCodeSelector } from 'store/modules/slotGameFlow/selectors'
import { openSnackbar } from 'store/modules/snackbar/actions'

import { setBonusesLoading } from '../bonusesFlow/actions'
import { getActiveCurrencyIdSelector } from '../currencies/selectors'
import { getJackpotOptInSelector } from '../jackpots/selectors'
import { actions as uiActions } from '../ui/slice'

import {
  LOYALTY_LAST_ACTIVITY_KEY,
  LOYALTY_INACTIVITY_SHOWN_KEY,
  isLastActivitySessionInitialized,
  isLastActivitySessionInitializing,
  startLastActivitySessionInit,
  setLastActivitySessionInitialized,
  failLastActivitySessionInit,
} from './loyaltyKeys'
import {
  getIsSoftKYCAvailableSelector,
  getSentWelcomeEmailFieldSelector,
  getUserIdSelector,
  getUserLegalRulesModalSettingSelector,
  getUserLegalRulesTypeSelector,
  getUserSelector,
  getUserVipPointsLevelSelector,
  isLoggedInSelector,
} from './selectors'
import { actions } from './slice'

import type { ILevel, Password, ResetPasswordManually, User } from '@patrianna/shared-patrianna-types/store/UserModule'
import type {
  AcceptRulesRequest,
  ChangePasswordRequest,
  GetAccountInfoRequest,
  GetLiveChatSettingsRequest,
  OptOutArbitrationRequest,
  OptInPromoRequest,
  ResetPasswordManuallyRequest,
  SendWelcomeEmailRequest,
  SetAccountAttributionInfoRequest,
  SetAccountInfoRequest,
} from '@patrianna/shared-patrianna-types/websocket/requests'
import type {
  AcceptRulesResponse,
  ChangePasswordResponse,
  GetAccountInfoResponse,
  GetLiveChatSettingsResponse,
  OptOutArbitrationResponse,
  OptInPromoResponse,
  SendWelcomeEmailResponse,
  SetAccountAttributionInfoResponse,
  SetAccountInfoResponse,
  SetAccountModeResponse,
} from '@patrianna/shared-patrianna-types/websocket/response'
import type { TypedThunk } from 'store/types'
import type { ProcessDynamoLinkResponse } from 'temp/api/client'

const t = getTranslationFromJson([content])

export const {
  setFirstDepositDate,
  setIsEmailVerified,
  setSentEmail,
  setUser,
  setUserCountry,
  setUserFlowIsRunning,
  setVipLevel,
  setXPLevel,
  deleteUser,
  setIsUpdateLegals,
  setPaymentMetaDataInfo,
  setLiveChatSettings,
  setSocialAuthData,
  removeSocialAuthData,
  setUserPasswordChanged,
  setTabsConfig,
  setLocationStatus,
  setLastActivity,
  setLoyaltyInactivityShown,
  setSignUpFraudInfo,
  setAccountMode,
} = actions

// export const changeVipLvl = (points: number) => (dispatch: Function, getState: () => ApplicationState) => {
//   const vipState: VipLevel = getUserVipPointsLevelSelector(getState())

//   dispatch(
//     setVipLevel({
//       ...vipState,
//       current: vipState.current + points
//     })
//   )
// }

export const setLevelProgress =
  (levelInfo: ILevel): TypedThunk =>
  (dispatch, getState) => {
    const { category } = levelInfo
    const gameCode = getActiveGameCodeSelector(getState())
    const optInGCjackpot = getJackpotOptInSelector(getState())
    const activeCurrency = getActiveCurrencyIdSelector(getState())
    const currentVipLevel = getUserVipPointsLevelSelector(getState()).name
    const pathname = window.location.pathname
    const isGamePage = isPlayPage(pathname)
    const hasAnyDialogOpen = !!getDialogStack()?.length

    switch (category) {
      case 'free_level': {
        dispatch(setXPLevel({ xpLevel: levelInfo }))
        if (levelInfo.levelChanged) {
          if (!optInGCjackpot || activeCurrency !== 'SC') {
            dispatch(levelUpHandler(levelInfo))
          }

          trackEvent('level_up', { category: gameCode, label: levelInfo.level.toString() })
          dispatch(addUnlockedGames(levelInfo?.unlocked))
        }
        break
      }
      case 'vip_level': {
        if (currentVipLevel !== levelInfo.name && IS_BRAND_PULSZ) {
          if (isGamePage && !hasAnyDialogOpen) {
            openDialog({ dialogName: 'LOYALTY_LEVEL_UP_DIALOG' })
          } else {
            dispatch(uiActions.setPendingDialog({ dialogName: 'LOYALTY_LEVEL_UP_DIALOG' }))
          }
        }
        dispatch(setVipLevel({ vipLevel: levelInfo }))
        dispatch(addUnlockedGames(levelInfo?.unlocked))
        break
      }
      default: {
        break
      }
    }
  }

export {
  LOYALTY_LAST_ACTIVITY_KEY,
  LOYALTY_INACTIVITY_SHOWN_KEY,
  setLastActivitySessionInitialized,
  resetLastActivitySession,
} from './loyaltyKeys'

export const getUserPaymentMetaData =
  (callback?: () => void): TypedThunk =>
  (dispatch, getState, { payment, errorHandler, api }) => {
    if (sessionStorage.getItem(LOYALTY_INACTIVITY_SHOWN_KEY)) {
      dispatch(setLoyaltyInactivityShown())
    }

    const storedLastActivity = localStorage.getItem(LOYALTY_LAST_ACTIVITY_KEY)
    if (storedLastActivity) {
      dispatch(setLastActivity({ lastActivity: storedLastActivity }))
    }

    // Read lastActivity from getAccountMeta only once per session (return flow).
    // On login this is skipped because authMiddleware already calls
    // setLastActivitySessionInitialized() after saving the signin response value.
    // The initializing guard prevents a race condition from parallel calls.
    if (!isLastActivitySessionInitialized() && !isLastActivitySessionInitializing()) {
      startLastActivitySessionInit()
      api
        .getAccountMeta({})
        .then((response) => {
          setLastActivitySessionInitialized()
          if (!response.lastActivity) {
            return
          }
          localStorage.setItem(LOYALTY_LAST_ACTIVITY_KEY, response.lastActivity)
          dispatch(setLastActivity({ lastActivity: response.lastActivity }))
        })
        .catch(() => {
          failLastActivitySessionInit()
        })
    }

    return payment
      .getPaymentMetaInfo({})
      .then(({ firstDeposit, lastDeposit, firstWithdraw }) => {
        dispatch(setPaymentMetaDataInfo({ firstDeposit, lastDeposit, firstWithdraw }))
        callback?.()
      })
      .catch(convertOpenApiErrorResponseToGateway('GetPaymentMetaInfoResponse'))
      .catch((err) => {
        if (err?.status?.errorCode !== 'err_timeout') {
          dispatch(errorHandler(err, {}))
        }
      })
  }

export const getAccountInfo =
  (): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: GetAccountInfoRequest = {
      type: 'GetAccountInfoRequest',
    }

    gateway
      .emit<GetAccountInfoResponse>(data)
      .then((body) => {
        const { balances, prerequisites, ...restUserData } = body

        dispatch(initCurrencies({ currencies: balances }))
        dispatch(setOpenPrerequisites({ prerequisites }))
        dispatch(setUser({ data: restUserData }))
        // PLDV-8952: the customer's location must reflect the last GetAccountInfoResponse.
        if (body.location) {
          dispatch(saveLocationData({ location: body.location }))
        }
        dispatch(getUserPaymentMetaData())
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
      .finally(() => {
        dispatch(setUserFlowIsRunning({ userFlowIsRunning: false }))
      })
  }
export const getLiveChatSettingsRequest =
  (): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: GetLiveChatSettingsRequest = {
      type: 'GetLiveChatSettingsRequest',
    }
    gateway
      .emit<GetLiveChatSettingsResponse>(data)
      .then((body) => {
        const { showLiveChat, departments, tag, vip, purchaseFlowEnabled, email, external_id } = body
        dispatch(
          setLiveChatSettings({
            liveChatSettings: {
              showLiveChat,
              departments,
              tag,
              vip,
              purchaseFlowEnabled,
              email,
              external_id,
            },
          }),
        )
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
  }

export const getSignUpFraudInfo =
  (): TypedThunk =>
  async (dispatch, _, { fraud, errorHandler }) => {
    const requestData = {
      type: 'GetSignUpFraudInfoRequest',
      brandName: process.env.BRAND_NAME,
      platform: 'web',
    }

    const fraudService = fraud as unknown as { getSignUpFraudInfo: (data: typeof requestData) => Promise<any> }

    fraudService
      .getSignUpFraudInfo(requestData)
      .then((response) => {
        dispatch(setSignUpFraudInfo(response))

        if (response.geoVerificationRequired) {
          dispatch(setLocationStatus({ locationStatus: 'FORCE_GPS' }))
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err, requestData))
        dispatch(setSignUpFraudInfo({ fraudCheckCompleted: false, geoVerificationRequired: false }))
      })
  }

export const updatePassword =
  (formData: Password): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: ChangePasswordRequest = {
      type: 'ChangePasswordRequest',
      oldPassword: formData.oldPassword,
      newPassword: formData.password,
    }

    gateway
      .emit<ChangePasswordResponse>(data)
      .then(() => {
        dispatch(setUserPasswordChanged())
        dispatch(
          openSnackbar({
            message: 'Password changed successfully. Please login again',
            variant: 'success',
          }),
        )
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
  }

export const resetPassword =
  (
    formData: ResetPasswordManually,
    queryParams: { rk: string; token: string },
    setSubmitting: (arg: boolean) => void,
    goToLogin: () => void,
  ): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: ResetPasswordManuallyRequest = {
      type: 'ResetPasswordManuallyRequest',
      rk: queryParams.rk,
      token: queryParams.token,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    }

    gateway
      .emit<ChangePasswordResponse>(data)
      .then(() => {
        dispatch(deleteUser())
        goToLogin()
        dispatch(
          openSnackbar({
            message: 'Password changed successfully. Please login again',
            variant: 'success',
          }),
        )
        setSubmitting(false)
        dispatch(setDataToLocalStorage(IS_SUCCESS_PASSWORD_CHANGE, true, false))
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
        setSubmitting(false)
      })
  }

// updateAccount = (info) => {
//   const { emitWS } = this.props;

//   emitWS('SetAccountInfoRequest', info);
// };

// uploadAccountWithImage = () => {
//   const {
//     _formData,
//     updateAccount,
//   } = this.props;
//   const contentType = this.getConentType(_formData.profilePhotо.name);

//   fetch(api.UPLOAD_IMAGE, {
//     method: 'POST',
//     credentials: 'include',
//     headers: {
//       'Content-Type': `image/${contentType}`,
//       'Access-Control-Expose-Headers': 'Location',
//     },
//     body: _formData.profilePhotо,
//   })
//     .then(data => {
//       const profilePhotо = data.headers.get('location');
//       updateAccount(normalizers.beforeSubmit({
//         ..._formData,
//         profilePhotо: `${api.config.GATEWAY}${profilePhotо}`,
//       }));
//     })
//     .catch(console.log);
// }

export const getDataAfterChangeAppState = (): TypedThunk => (dispatch, getState) => {
  const isLoggedIn = isLoggedInSelector(getState())
  if (isLoggedIn) {
    dispatch(getAccountInfo())
    dispatch(getConfirmedKycInfo())
  }
}

export const openWelcomeEmailDialog =
  (timerId: ReturnType<typeof setTimeout>): TypedThunk =>
  (dispatch, getState) => {
    const isEmailSent = getSentWelcomeEmailFieldSelector(getState())
    const isSCEnabled = sweepstakeEnabledSelector(getState())
    const dialogStack = getDialogStack()

    if (dialogStack.length > 0) {
      // to prevent modal overlap and let it render at the end of stack
      removeShownSessionDialog('WELCOME_EMAIL_SEND_DIALOG')

      return
    }

    if (!isEmailSent && isSCEnabled) {
      dispatch(setDataToLocalStorage('WELCOME_EMAIL_DIALOG_TIMER_ID', timerId))
      trackEvent('welcome_popup_appeared')
      openDialog({ dialogName: 'WELCOME_EMAIL_SEND_DIALOG' })
    }
  }

export const openWelcomeEmailDialogWithDelay =
  (delay: number = 180000): TypedThunk =>
  (dispatch) => {
    const timerId = setTimeout(() => {
      dispatch(openWelcomeEmailDialog(timerId))
    }, delay)
  }

export const sendEmailRequest =
  (): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: SendWelcomeEmailRequest = {
      type: 'SendWelcomeEmailRequest',
    }

    gateway
      .emit<SendWelcomeEmailResponse>(data)
      .then(() => {
        dispatch(setSentEmail())
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
  }

export const accountModeChangedHandler =
  (body: SetAccountModeResponse | SetAccountAttributionInfoResponse): TypedThunk =>
  (dispatch) => {
    const isScEnabled = body?.sweepstake

    dispatch(enabledSweepstake(isScEnabled))
    dispatch(setUser({ data: body.account }))
    dispatch(getOffers())

    if (isScEnabled) {
      dispatch(setActiveCurrency({ activeCurrencyId: 'SC' }))
      dispatch(getAccountBonusesRequest())
    }
  }

export const setAccountModeAction =
  (mode: User['mode']): TypedThunk =>
  (dispatch) => {
    dispatch(setAccountMode({ mode }))
  }

export const acceptLegals =
  (): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const isScAvailable = sweepstakeEnabledSelector(getState())
    const types = getUserLegalRulesTypeSelector(getState())

    const data: AcceptRulesRequest = {
      type: 'AcceptRulesRequest',
      types,
    }

    dispatch(setIsUpdateLegals({ isUpdateingLegals: true }))

    if (process.env.BRAND === 'pulszbingo' && isScAvailable) {
      // DAILY_REWARD_DIALOG force add
      // to follow the right order of expected modals
      dispatch(setBonusesLoading(true))
    }

    gateway
      .emit<AcceptRulesResponse>(data)
      .then((body) => {
        if (body.applied) {
          dispatch(setUser({ data: body.account }))

          if (isScAvailable) {
            dispatch(getAccountBonusesRequest())
          }

          dispatch(setIsUpdateLegals({ isUpdateingLegals: false }))
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
  }

export const updateUser =
  (formData: Partial<User>, callback?: () => void): TypedThunk =>
  async (dispatch, getState, { gateway, errorHandler }) => {
    const session = await getSeonSessionId()

    const data: SetAccountInfoRequest = {
      type: 'SetAccountInfoRequest',
      session,
      ...formData,
    }

    gateway
      .emit<SetAccountInfoResponse>(data)
      .then((body) => {
        dispatch(setUser({ data: body }))
        callback?.()
      })
      .catch((error) => {
        dispatch(errorHandler(error, data))

        if (error?.status?.errorText) {
          dispatch(
            openSnackbar({
              message: error.status.errorText,
            }),
          )
        }
      })
      .finally(() => {
        dispatch(setUserFlowIsRunning({ userFlowIsRunning: false }))
      })
  }

export const deeplinkModeHandler =
  (advertisingId: string, osVersion: string, appVersion: string): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const loggedIn = isLoggedInSelector(getState())

    if (loggedIn) {
      const data: SetAccountAttributionInfoRequest = {
        type: 'SetAccountAttributionInfoRequest',
        advertisingId,
        osVersion,
        appVersion,
      }

      gateway
        .emit<SetAccountAttributionInfoResponse>(data)
        .then((body) => {
          dispatch(accountModeChangedHandler(body))
        })
        .catch((err) => {
          dispatch(errorHandler(err, data))
        })
    }
  }

export const checkLegalsModalVisibility = (): TypedThunk => (dispatch, getState) => {
  const legalSetting = getUserLegalRulesModalSettingSelector(getState())

  const acceptLegalsUpdateSilently = () => {
    dispatch(acceptLegals())
    dispatch(setIsUpdateLegals({ isUpdateingLegals: true }))
  }

  if (legalSetting === 'Nothing') {
    acceptLegalsUpdateSilently()
  } else {
    openDialog({ dialogName: 'LEGALS_MODAL' })
  }
}

export const openSoftKYCFlow =
  (callBack: () => void, isKycDisable?: boolean): TypedThunk =>
  (dispatch, getState) => {
    const isKYCAvailable = getIsSoftKYCAvailableSelector(getState())

    if (isKycDisable) {
      callBack()

      return null
    }

    if (!isKYCAvailable) {
      openDialog({ dialogName: 'SOFT_KYC_DIALOG', dialogProps: { callBack } })

      return null
    }
    callBack()

    return null
  }

export const trackKYCConfirmedEvent =
  (eventName: 'ID_Verification_Success' | 'POA_Verification_Success'): TypedThunk =>
  (dispatch, getState) => {
    const userId = getUserIdSelector(getState())
    trackEvent(eventName, { category: userId })
  }

export const getAccountInfoAndUpdateVerificationData =
  (kycStatus: User['kycStatus']): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const user = getUserSelector(getState())
    const data: GetAccountInfoRequest = { type: 'GetAccountInfoRequest' }

    gateway
      .emit<GetAccountInfoResponse>(data)
      .then(({ softKycInfo, status }) => {
        dispatch(setUser({ data: { ...user, softKycInfo, status } }))
        dispatch(getConfirmedKycInfo())
      })
      .catch((err) => dispatch(errorHandler(err, data)))
  }

export const optOutArbitration =
  ({
    setLoading,
    setRejected,
  }: {
    setLoading: (loading: boolean) => void
    setRejected: (rejected: boolean) => void
  }): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: OptOutArbitrationRequest = {
      type: 'OptOutArbitrationRequest',
    }

    const rejectedMessages = {
      ALREADY_OPTED_OUT: 'Your request has already been opted-out',
      SIGN_UP_IS_OLDER_THAN_30_DAYS:
        'Your request has been made more than 30 days after registration. See email for details',
    }

    setLoading(true)

    gateway
      .emit<OptOutArbitrationResponse>(data)
      .then((body) => {
        if (body.rejectionReason) {
          setRejected(true)
          dispatch(openSnackbar({ message: rejectedMessages[body.rejectionReason] }))
        }
      })
      .catch((err) => {
        dispatch(errorHandler(err, data))
      })
      .finally(() => {
        setLoading(false)
      })
  }

export const sendOptInPromoRequest =
  (utmCode: string, location: string): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const data: OptInPromoRequest = {
      type: 'OptInPromoRequest',
      utmCode: utmCode,
      optIn: true,
      location,
    }

    gateway.emit<OptInPromoResponse>(data).catch((err) => {
      dispatch(errorHandler(err, data))
    })
  }

export const updateCommunicationPreferences =
  (
    preferences?: {
      doNotSendSms?: boolean
      doNotCall?: boolean
      doNotSendEmails?: boolean
      doNotSendPushes?: boolean
    },
    source: 'home_page_banner' | 'home_page_nudge' | 'daily_login_rewards' | 'my_profile' = 'my_profile',
    callback?: () => void,
  ): TypedThunk =>
  async (dispatch, getState, { gateway, errorHandler }) => {
    const data = {
      type: 'SetAccountCommunicationPreferencesRequest',
      source,
      doNotSendEmails: preferences?.doNotSendEmails,
      doNotSendPushes: preferences?.doNotSendPushes,
      doNotSendSms: preferences?.doNotSendSms,
      doNotCall: preferences?.doNotCall,
    }

    gateway
      .emit<{ account: any }>(data)
      .then((body) => {
        if ('account' in body) {
          dispatch(
            setUser({
              data: {
                ...body.account,
                doNotSendSms: preferences.doNotSendSms,
                doNotCall: preferences.doNotCall,
                doNotSendEmails: preferences.doNotSendEmails,
                doNotSendPushes: preferences.doNotSendPushes,
              },
            }),
          )
          callback?.()
        }
        dispatch(getAccountInfo())
      })
      .catch((error) => {
        dispatch(errorHandler(error, data))
        if (error?.status?.errorText) {
          dispatch(
            openSnackbar({
              message: error.status.errorText,
            }),
          )
        }
      })
  }

export const openLoyaltyInactivityDialog = (): TypedThunk => () => {
  openDialog({ dialogName: 'LOYALTY_INACTIVITY_DIALOG' })
}

export const dynamoUrlHandler =
  (dynamoUtm: string, tokenFromUrl: string, utmCode?: string): TypedThunk =>
  (dispatch, getState, { api, errorHandler }) => {
    const loggedIn = isLoggedInSelector(getState())
    const getDynamoLinkRejectionMessage = (reason: ProcessDynamoLinkResponse['rejectionReason']): string => {
      if (reason === 'LINK_ALREADY_USED') {
        return t('dynamo_link_used_error_message')
      }

      return t('dynamo_link_not_valid_error_message')
    }

    const data = {
      type: 'ProcessDynamoLinkRequest',
      linkId: dynamoUtm,
    }

    if (loggedIn) {
      api
        // @ts-ignore
        .processDynamoLink({ linkId: dynamoUtm, token: tokenFromUrl, ...(utmCode ? { utmCode } : {}) })
        .then((res) => {
          if (res && 'rejectionReason' in res) {
            const message = getDynamoLinkRejectionMessage(res.rejectionReason)
            throw new Error(message)
          }
        })
        .catch((error) => {
          dispatch(errorHandler(error, data))
          setTimeout(() => {
            dispatch(
              openSnackbar({
                message: error instanceof Error ? error.message : t('dynamo_link_not_valid_error_message'),
                errorReasonCode: ERROR_REASON_CODE_MAP.OTP_VERIFY_DAILY_LIMIT,
              }),
            )
          }, 1000)
        })
    }
  }
